import { DOCUMENT, Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import {
  configurationData,
  configurationSchema,
} from '@shared/club-configurations/configuration-data';
import { ZodError } from 'zod';
import { Configuration, EntityKeys } from '../models/configuration.model';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  public environment: EntityKeys;
  public configuration: Configuration;
  public defaultConfig = configurationData['default'];
  private _injector = inject(Injector);
  private _document = inject(DOCUMENT);
  private _domDocument = document;
  public provenueEnvironment: string;

  public init = () => {
    const url = new URL(window.location.href);
    const clientName = url.searchParams.get('load-mock-client-name');

    let client: string;
    if (environment.sandboxClient) {
      client = ConfigurationService.resolveSandboxAwareClient(
        ConfigurationService.extractHostnameClient(url.href),
        clientName,
        environment.sandboxClient
      );
      this.environment = client as EntityKeys;
    } else {
      client = this.getEnvironmentFromHostname(clientName);
    }

    if (!this.provenueEnvironment) {
      this.provenueEnvironment = url.searchParams.get('load-pv-env') ?? '';
    }

    let configuration =
      configurationData[client as keyof typeof configurationData];
    const clientKey = client as keyof typeof configurationData;
    const parseResult = configurationSchema.safeParse(configuration);
    const isLocalEnvironment = location.href.includes('local');
    const isClientMissing = typeof configurationData[clientKey] === 'undefined';
    const isDefaultClient = client === 'default';

    if (!parseResult.success) {
      this.logConfigurationSchemaErrors(client, parseResult.error);
    }

    if (!parseResult.success || isDefaultClient || isClientMissing) {
      this.configuration = this.fallbackToDefaultConfig();
      if (!isLocalEnvironment) {
        this.navigateToNotFound();
        return;
      }
    }

    configuration = configuration ?? this.configuration;

    this._document
      .getElementById('appFavicon')!
      .setAttribute('href', configuration.favicon);
    this.fillWithEnvConfiguration(configuration);
    this.declareGlobalCssVariables();
  };

  public addGAToConfiguration(gaIds: string[]) {
    if (!this.configuration.dvmData.gaSectorsIdArray.length) {
      const gaSectorIds = gaIds.filter(node => !node.includes('ADA'));
      this.configuration.dvmData.gaSectorsIdArray = gaSectorIds;
    }
    if (!this.configuration.dvmData.gaAdaIdArray.length) {
      const gaAdaIds = gaIds.filter(node => node.includes('ADA'));
      this.configuration.dvmData.gaAdaIdArray = gaAdaIds;
    }
  }

  public static extractHostnameClient(href: string): string | undefined {
    const regex: RegExp =
      /^(?:http|https):\/\/(?:local\.)?(?:private\.)?(?:demo-)?(?:dev-)?(?:pre-)?(?:uat-)?(.*)?(?:\.groupsales)?\.3ddigitalvenue\.com.*/gm;
    const matches = regex.exec(href);
    return matches?.[1]?.split('.')?.[0];
  }

  public static resolveSandboxAwareClient(
    hostnameClient: string | undefined,
    mockParam: string | null,
    sandboxClient: string
  ): string {
    const isRegisteredClub = (client: string | null | undefined): boolean =>
      !!client &&
      client !== 'default' &&
      Object.prototype.hasOwnProperty.call(configurationData, client);

    if (isRegisteredClub(mockParam)) {
      return mockParam as string;
    }
    if (isRegisteredClub(hostnameClient)) {
      return hostnameClient as string;
    }
    return sandboxClient;
  }

  private getEnvironmentFromHostname(clientName: string | null) {
    const client = ConfigurationService.extractHostnameClient(
      location.href
    ) as EntityKeys;
    const isLocalEnvironment = false;

    if ((!isLocalEnvironment && client === 'default') || client == null) {
      if (clientName) {
        this.environment = clientName as EntityKeys;
        return clientName;
      }

      this.configuration = this.fallbackToDefaultConfig()!;
      this.navigateToNotFound();
    } else {
      this.environment = client;
    }

    return client;
  }

  private fillWithEnvConfiguration(configuration: Configuration) {
    this.configuration = {} as any;

    for (const [key, value] of Object.entries(configuration)) {
      (this.configuration as any)[key] = value;
    }
  }

  private declareGlobalCssVariables() {
    const root: HTMLElement | null = this._domDocument.querySelector(':root');

    if (root) {
      const theme = this.configuration.theme;
      const dvmStyles = this.configuration.dvmStyles;
      const mergedStyles = Object.assign({}, theme, {
        'seat-available-none-fill': dvmStyles['seat-available-none-fill'],
        'seat-available-pending': dvmStyles['seat-available-pending'],
        'client-dvm-map': dvmStyles['map-height'],
      });

      for (const [key, value] of Object.entries(mergedStyles)) {
        if (typeof value === 'string') {
          root.style.setProperty('--' + key, value);
        }
      }
    }
  }

  private fallbackToDefaultConfig(): Configuration {
    this.environment = 'default';
    return configurationData['default']!;
  }

  private logConfigurationSchemaErrors(
    client: EntityKeys | string,
    error: ZodError
  ): void {
    const issues = error.issues.map(issue => {
      const path = issue.path.map(segment => String(segment)).join('.');
      return {
        path,
        code: issue.code,
        message: issue.message,
        received: issue.input,
      };
    });

    console.warn('[GS club-config] Zod validation failed', {
      client,
      hostname: window.location.hostname,
      issueCount: issues.length,
      issues,
    });
  }

  private navigateToNotFound() {
    const router = this._injector.get(Router);
    router.navigate(['not-found']);
  }
}
